import Axios from "axios";

import { Response } from "@konv/http";
import Bugsnag from "@bugsnag/js";
import { AndroidWebviewBridge } from "@/tools/AndroidWebviewBridge";
import { Logger } from "@/tools/Logger";
import { LockedTableException } from "@/services/expections/LockedTableException";
import { PosPendencyException } from "@/services/expections/PosPendencyException";

export class ColibriAPI {
  constructor({ host, port, apiKey, user, password }) {
    this.baseURL = ColibriAPI.buildBaseUrl({ host, port });
    this.apiToken = ColibriAPI.buildToken({ user, password });
    this.apiKey = apiKey || "D756CD36-CB99-4F2F-B4A1-932966F7A44B";

    this.client = Axios.create({
      baseURL: this.baseURL,
      timeout: 1000 * 60 * 2,
      headers: {
        api_key: this.apiKey,
        Authorization: this.apiToken,
      },
    });
  }

  static buildBaseUrl({ host, port }) {
    if (host.startsWith("http://") || host.startsWith("https://")) {
      return host;
    }

    return `http://${host}:${port}`;
  }

  static buildToken({ user, password }) {
    const encodedToken = btoa(`${user}:${password}`);
    return `Basic ${encodedToken}`;
  }

  async doRequest({ endpoint, method, headers, data, query, log = true }) {
    try {
      const queryParams = query ? { params: query } : {};

      const androidBridge = await AndroidWebviewBridge.getInstance();
      if (androidBridge.loaded) {
        const response = await androidBridge.request({
          url: [this.baseURL, endpoint].join(""),
          method,
          headers: {
            ...headers,
            api_key: this.apiKey,
            Authorization: this.apiToken,
          },
          data,
          query,
        });

        if (log) {
          Logger.info("ColibriAPI", {
            request: {
              url: endpoint,
              method,
              data,
              query,
            },
            response: {
              code: response.getCode(),
              data: response.getData(),
              errors: response.getErrors([]),
            },
          });
        }

        return this.buildResponse(response.getData({}));
      }

      const response = await this.client.request({
        url: endpoint,
        method,
        data,
        headers: {
          ...headers,
        },
        ...queryParams,
      });

      Bugsnag.leaveBreadcrumb(
        "Colibri - Requisição",
        {
          url: endpoint,
          method,
          data,
          query,
          status: response?.status,
          response: response?.data,
        },
        "request"
      );

      return this.buildResponse(response?.data || {});
    } catch (err) {
      console.error("Colibri - Erro na requisição", err.message, err);

      if (log) {
        Logger.error("Colibri", {
          request: {
            url: endpoint,
            method,
            data,
            query,
          },
          response: {
            status: err?.response?.status,
            response: err?.response?.data,
          },
          error: {
            message: err.message,
            stack: err.stack,
          },
        });
      }

      Bugsnag.leaveBreadcrumb(
        "Colibri - Requisição",
        {
          url: endpoint,
          method,
          data,
          query,
          err: err?.message,
          status: err?.response?.status,
          response: err?.response?.data,
        },
        "request"
      );

      if (err.isAxiosError) {
        const response = err?.response?.data;

        if (!response) {
          return new Response(null, 1000, [`Falha na rede`]);
        }

        return this.buildResponse(err?.response?.data || {});
      }

      return new Response(null, 1000, [err.message]);
    }
  }

  async buildResponse (response) {
    const status = response.status?.codigo;
    if (status >= 400) {
      const errorCode = response?.status?.tipo;
      const errorMessage = response?.status?.mensagem || "Erro desconhecido";

      if (errorCode === "XBloqueioPorLock") {
        throw new LockedTableException();
      }

      if (errorCode === "XPeriodoDeveSerFechado") {
        throw new PosPendencyException();
      }

      return new Response(null, 1000, [`${errorCode} - ${errorMessage}`]);
    }

    return new Response(response, 200, []);
  }

  async addItemToOrder({ table, mode, origin, items }) {    
    const response = await this.doRequest({
      endpoint: "/v1/consumos/",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      query: {
        codigo: table,
        modo_venda: mode,
        origem: origin,
      },
      data: {
        itens: items,
      },
    });

    return response;
  }

  async getOperationFromTable({ table, mode, origin }) {
    return await this.doRequest({
      endpoint: `/v1/consumos/operacao`,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      query: {
        codigo: table,
        modo_venda: mode,
        origem: origin,
      },
      data: {},
    });
  }
}
